import React, { useState } from "react";
import { experienceData } from "../data/mock";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Briefcase, ChevronDown, ChevronUp } from "lucide-react";

const ExperienceCard = ({ item, index }) => {
  const [expanded, setExpanded] = useState(index === 0);

  return (
    <div className="relative flex gap-6 lg:gap-8">
      {/* Timeline line */}
      <div className="hidden sm:flex flex-col items-center">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
            item.current
              ? "bg-teal-50 border-teal-400 text-teal-600"
              : "bg-white border-slate-200 text-slate-400"
          }`}
        >
          <Briefcase size={16} />
        </div>
        {index < experienceData.length - 1 && (
          <div className="w-px flex-1 bg-slate-200 mt-2" />
        )}
      </div>

      {/* Card */}
      <Card
        className={`flex-1 mb-6 border transition-all duration-300 cursor-pointer hover:shadow-md ${
          item.current
            ? "border-teal-100 shadow-sm bg-white"
            : "border-slate-100 bg-white hover:border-slate-200"
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <CardContent className="p-5 lg:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-semibold text-slate-900">
                  {item.role}
                </h3>
                {item.current && (
                  <Badge className="bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-50 text-xs">
                    Current
                  </Badge>
                )}
              </div>
              <p className="text-sm text-slate-500">
                {item.company} &middot; {item.period}
              </p>
            </div>
            <button className="text-slate-400 hover:text-slate-600 transition-colors mt-1 shrink-0">
              {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>

          {expanded && (
            <div className="mt-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
              <ul className="space-y-2.5">
                {item.highlights.map((point, i) => (
                  <li key={i} className="flex gap-3 text-sm text-slate-600 leading-relaxed">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-400 mt-2 shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>

              {/* Projects sub-section removed - now in dedicated section */}

              <div className="flex flex-wrap gap-2 pt-1">
                {item.tags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="text-xs font-normal bg-slate-50 text-slate-600 border border-slate-100 hover:bg-slate-100"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const Experience = () => {
  return (
    <section id="experience" className="py-14 lg:py-20 bg-slate-50/50">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-12 lg:mb-16">
          <p className="text-sm font-medium text-teal-600 tracking-wide uppercase mb-2">
            Career Journey
          </p>
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
            Professional Experience
          </h2>
          <p className="mt-3 text-slate-500 leading-relaxed">
            A decade of progressive growth at J.B. Hunt Transport Services, from operational analysis to fleet and account management.
          </p>
        </div>

        <div className="max-w-3xl">
          {experienceData.map((item, index) => (
            <ExperienceCard key={item.id} item={item} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
